import { Input } from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { Controller, useFormContext } from 'react-hook-form';
import { useAuth } from '@/app/hooks/auth-provider';
import { useFetchAccountQuery } from '@/slices/account-api-slice.ts';
import type { AccountSiteStaffMemberModel } from '../../types/account-site-staff-member.type.ts';
import type { StaffMemberModel } from '../../types/staff-member.type.ts';
import { UserTypeEnum } from '../../types/user-type.ts';
import { SpecialtiesSelect } from '../specialties-select.tsx';
import StaffMemberCategorySelector from '../staff-member-category-selector.tsx';
import { StaffRoleSelect } from '../staff-role-select.tsx';

const AccountSiteStaffMemberFormFields = ({
  data,
  name,
}: {
  data: AccountSiteStaffMemberModel;
  name: string;
}) => {
  const { user } = useAuth();
  const { control } = useFormContext();
  const { data: account } = useFetchAccountQuery(data?.account?.key ?? skipToken);

  const showPermissions = () => {
    if (user?.userType === UserTypeEnum.Admin) {
      return true;
    }

    const currentStaffUser = user as StaffMemberModel;
    const roleId = currentStaffUser?.accountSiteStaffMembers.find(x => x.accountId === account?.id)
      ?.staffRole.id;

    return data?.staffRole?.id ?? 0 <= roleId;
  };

  return (
    <div className='flex flex-col gap-4'>
      {showPermissions() && <StaffRoleSelect name={`${name}.staffRole`} required={true} />}
      <Controller
        name={`${name}.position`}
        control={control}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            label='Position'
            variant='bordered'
            placeholder='Physician, Surgeon, Director...'
            isInvalid={fieldState.invalid}
            errorMessage={fieldState.error?.message}
          />
        )}
      />
      <StaffMemberCategorySelector accountId={account?.id} name={`${name}.staffMemberCategory`} />
      <SpecialtiesSelect
        accountId={account?.id}
        whiteListSpecialties={account?.specialties ?? []}
        name={`${name}.specialties`}
      />
    </div>
  );
};

export default AccountSiteStaffMemberFormFields;
